import { useState, useRef, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import {
  Plus, Trash2, Video, Play, X, Edit3, Save, Loader2,
  Upload, Film, Clock, HardDrive, FileVideo, Image as ImageIcon,
  FileText, File as FileIcon, Download, Globe, Lock, RefreshCw, Music, Pause, Eye
} from 'lucide-react'
import {
  sharedMediaApi,
  adminApi,
  type SharedMediaResponse,
  type SharedMediaType,
  type SharedMediaVisibility,
  type OrganizationSimple,
  type SharedMediaAccessOrg,
} from '@/api/admin'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@quebon/ui/components/button'
import { SharedMediaOrderDialog } from '@/components/sharedMedia/SharedMediaOrderDialog'

// ─── Utility Functions ───

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '--:--'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

export function normalizeSharedMediaTitle(title: string): string | null {
  const normalized = title.trim()
  return normalized.length > 0 ? normalized : null
}

const TABS: { key: SharedMediaType; label: string; icon: typeof Video }[] = [
  { key: 'video', label: '동영상', icon: Video },
  { key: 'text', label: '텍스트', icon: FileText },
  { key: 'image', label: '이미지', icon: ImageIcon },
  { key: 'file', label: '파일', icon: FileIcon },
]

const VISIBILITY_OPTIONS: { value: SharedMediaVisibility; label: string; description: string }[] = [
  { value: 'global', label: '전체 공개', description: '모든 조직에서 볼 수 있습니다.' },
  { value: 'company_all', label: 'Company 전체', description: '모든 Company 조직에 공개합니다.' },
  { value: 'learning_center_all', label: '러닝센터 전체', description: '모든 러닝센터에 공개합니다.' },
  { value: 'specific', label: '지정 조직', description: '업로드 후 공개할 조직을 선택합니다.' },
]

const MAX_GENERAL_FILE_SIZE_MB = 300

// ─── Main Page ───

export function SharedMediaLibraryPage() {
  const queryClient = useQueryClient()
  const [showOrderDialog, setShowOrderDialog] = useState(false)
  const [activeTab, setActiveTab] = useState<SharedMediaType>('video')

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['admin', 'shared-media', activeTab],
    queryFn: async () => {
      const response = await sharedMediaApi.getAll(activeTab)
      if (response.data.success && response.data.data) {
        return response.data.data
      }
      return []
    },
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'shared-media', activeTab] })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">자료실</h1>
          <p className="text-gray-600 mt-1">
            동영상, 텍스트, 이미지, 파일 자료를 관리합니다.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowOrderDialog(true)}>순서 조정</Button>
          <Button variant="ghost" size="icon" onClick={invalidate} aria-label="새로고침">
            <RefreshCw />
          </Button>
        </div>
      </div>

      {showOrderDialog && <SharedMediaOrderDialog onClose={() => setShowOrderDialog(false)} />}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {TABS.map(tab => {
            const Icon = tab.icon
            const isActive = activeTab === tab.key
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-amber-500 text-amber-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : activeTab === 'video' ? (
        <VideoTab items={items} onInvalidate={invalidate} />
      ) : activeTab === 'text' ? (
        <TextTab items={items} onInvalidate={invalidate} />
      ) : activeTab === 'image' ? (
        <ImageTab items={items} onInvalidate={invalidate} />
      ) : (
        <FileTab items={items} onInvalidate={invalidate} />
      )}
    </div>
  )
}

// ─── Video Tab ───

function VideoTab({ items, onInvalidate }: { items: SharedMediaResponse[]; onInvalidate: () => void }) {
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploadDescription, setUploadDescription] = useState('')
  const [uploadVisibility, setUploadVisibility] = useState<SharedMediaVisibility>('global')
  const [uploadProgress, setUploadProgress] = useState(false)
  const [playingItem, setPlayingItem] = useState<SharedMediaResponse | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [accessItem, setAccessItem] = useState<SharedMediaResponse | null>(null)

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!uploadFile) throw new Error('파일을 선택하세요')
      if (!uploadTitle.trim()) throw new Error('제목을 입력하세요')
      setUploadProgress(true)
      const response = await sharedMediaApi.uploadFile(uploadFile, uploadTitle.trim(), 'video', uploadVisibility, uploadDescription.trim() || undefined)
      return response.data.data
    },
    onSuccess: (data) => {
      onInvalidate()
      closeUploadModal()
      if (data?.visibility === 'specific') {
        setAccessItem(data)
        toast.success('동영상이 업로드되었습니다. 공개할 조직을 선택하세요.')
      } else {
        toast.success('동영상이 업로드되었습니다.')
      }
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || error.message || '업로드에 실패했습니다.')
    },
    onSettled: () => setUploadProgress(false),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, title, description }: { id: number; title: string; description: string }) => {
      const response = await sharedMediaApi.update(id, { title, description })
      return response.data.data
    },
    onSuccess: () => { onInvalidate(); toast.success('수정되었습니다.'); setEditingId(null) },
    onError: () => toast.error('수정에 실패했습니다.'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => { await sharedMediaApi.delete(id) },
    onSuccess: () => { onInvalidate(); toast.success('삭제되었습니다.') },
    onError: () => toast.error('삭제에 실패했습니다.'),
  })

  const closeUploadModal = () => {
    setShowUploadModal(false)
    setUploadFile(null)
    setUploadTitle('')
    setUploadDescription('')
    setUploadVisibility('global')
    setUploadProgress(false)
  }

  return (
    <>
      {/* Action */}
      <div className="flex justify-end">
        <button
          onClick={() => { setUploadFile(null); setUploadTitle(''); setUploadDescription(''); setShowUploadModal(true) }}
          className="flex items-center px-4 py-2 bg-amber-500 text-slate-900 font-medium rounded-lg hover:bg-amber-600 shadow-sm"
        >
          <Upload className="w-5 h-5 mr-2" />
          동영상 업로드
        </button>
      </div>

      {/* Grid */}
      {items.length === 0 ? (
        <EmptyState icon={Film} label="동영상이 없습니다" actionLabel="첫 동영상 업로드" onAction={() => setShowUploadModal(true)} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map(item => (
            <div key={item.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group hover:shadow-md transition-shadow">
              {/* Preview */}
              <div className="relative bg-slate-900 aspect-video flex items-center justify-center cursor-pointer group/play" onClick={() => setPlayingItem(item)}>
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 group-hover/play:bg-slate-900/60 transition-colors">
                  <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center group-hover/play:bg-white/30 transition-colors">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                </div>
                <FileVideo className="w-12 h-12 text-slate-600" />
                <div className="absolute bottom-2 left-2 right-2 flex justify-between text-xs text-white/70">
                  <span className="bg-black/50 px-2 py-0.5 rounded">
                    {item.contentType?.split('/')[1]?.toUpperCase() || 'VIDEO'}
                  </span>
                  <span className="bg-black/50 px-2 py-0.5 rounded">{formatFileSize(item.fileSize)}</span>
                </div>
                {item.durationSeconds && (
                  <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDuration(item.durationSeconds)}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-4">
                {editingId === item.id ? (
                  <div className="space-y-3">
                    <input type="text" value={editTitle} onChange={e => setEditTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500" placeholder="제목" autoFocus />
                    <textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none h-16 focus:ring-2 focus:ring-amber-500 focus:border-amber-500" placeholder="설명 (선택)" />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100">취소</button>
                      <button onClick={() => updateMutation.mutate({ id: item.id, title: editTitle, description: editDescription })} disabled={updateMutation.isPending} className="flex items-center px-3 py-1.5 text-sm bg-amber-500 text-slate-900 font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50">
                        <Save className="w-3.5 h-3.5 mr-1" />저장
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="font-semibold text-gray-900 mb-1 truncate" title={item.title}>{item.title}</h3>
                    {item.description && <p className="text-sm text-gray-500 mb-2 line-clamp-2">{item.description}</p>}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-400">
                          {new Date(item.createdAt).toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </p>
                        <VisibilityBadge visibility={item.visibility} onClick={() => setAccessItem(item)} />
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => { setEditingId(item.id); setEditTitle(item.title); setEditDescription(item.description || '') }} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg" title="수정"><Edit3 className="w-4 h-4" /></button>
                        <button onClick={() => { if (confirm('이 동영상을 삭제하시겠습니까?')) deleteMutation.mutate(item.id) }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="삭제"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && <div className="text-center text-sm text-gray-500">총 {items.length}개의 동영상</div>}

      {showUploadModal && (
        <FileUploadModal
          mediaType="video"
          file={uploadFile}
          title={uploadTitle}
          description={uploadDescription}
          visibility={uploadVisibility}
          uploading={uploadProgress}
          onFileSelect={setUploadFile}
          onTitleChange={setUploadTitle}
          onDescriptionChange={setUploadDescription}
          onVisibilityChange={setUploadVisibility}
          onUpload={() => uploadMutation.mutate()}
          onClose={closeUploadModal}
        />
      )}

      {playingItem && <VideoPlayerModal item={playingItem} onClose={() => setPlayingItem(null)} />}
      {accessItem && <AccessControlModal item={accessItem} onClose={() => setAccessItem(null)} onUpdate={onInvalidate} />}
    </>
  )
}

// ─── Text Tab ───

function TextTab({ items, onInvalidate }: { items: SharedMediaResponse[]; onInvalidate: () => void }) {
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editContent, setEditContent] = useState('')
  const [showCrawl, setShowCrawl] = useState(false)
  const [crawlTitle, setCrawlTitle] = useState('')
  const [crawlContent, setCrawlContent] = useState('')
  const [accessItem, setAccessItem] = useState<SharedMediaResponse | null>(null)

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await sharedMediaApi.createText({ title: '새 자료', content: '' })
      return response.data.data
    },
    onSuccess: (data) => {
      onInvalidate()
      if (data) {
        setEditingId(data.id)
        setEditTitle(data.title)
        setEditContent(data.content || '')
      }
    },
    onError: () => toast.error('자료 생성에 실패했습니다.'),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, title, content }: { id: number; title: string; content: string }) => {
      const response = await sharedMediaApi.update(id, { title, content })
      return response.data.data
    },
    onSuccess: () => { onInvalidate(); toast.success('자료가 저장되었습니다.'); setEditingId(null) },
    onError: () => toast.error('자료 저장에 실패했습니다.'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => { await sharedMediaApi.delete(id) },
    onSuccess: () => { onInvalidate(); toast.success('자료가 삭제되었습니다.') },
    onError: () => toast.error('자료 삭제에 실패했습니다.'),
  })

  return (
    <>
      <div className="flex justify-end">
        <button onClick={() => createMutation.mutate()} className="flex items-center px-4 py-2 bg-amber-500 text-slate-900 font-medium rounded-lg hover:bg-amber-600 shadow-sm">
          <Plus className="w-5 h-5 mr-2" />
          텍스트 추가
        </button>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={FileText} label="텍스트 자료가 없습니다" actionLabel="첫 자료 만들기" onAction={() => createMutation.mutate()} />
      ) : (
        <div className="space-y-4">
          {items.map(item => (
            <div key={item.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 group">
              {editingId === item.id ? (
                <div className="space-y-4">
                  <input type="text" value={editTitle} onChange={e => setEditTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-lg font-semibold focus:ring-2 focus:ring-amber-500 focus:border-amber-500" placeholder="제목을 입력하세요" autoFocus />
                  <textarea value={editContent} onChange={e => setEditContent(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg min-h-[200px] resize-y font-mono text-sm leading-relaxed focus:ring-2 focus:ring-amber-500 focus:border-amber-500" placeholder={'내용을 입력하세요...\n\nMarkdown 형식을 지원합니다.'} />
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setEditingId(null)} className="px-4 py-2 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100">취소</button>
                    <button onClick={() => updateMutation.mutate({ id: item.id, title: editTitle, content: editContent })} className="flex items-center px-4 py-2 bg-amber-500 text-slate-900 font-medium rounded-lg hover:bg-amber-600">
                      <Save className="w-4 h-4 mr-2" />저장
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{item.title}</h3>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-400">
                          {new Date(item.createdAt).toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <VisibilityBadge visibility={item.visibility} onClick={() => setAccessItem(item)} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button onClick={() => { setCrawlTitle(item.title); setCrawlContent(item.content || ''); setShowCrawl(true) }} className="flex items-center gap-1.5 px-3 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-700 transition-colors shadow-sm" title="영상 보기">
                        <Play className="w-4 h-4" />
                        <span className="text-sm font-medium">영상</span>
                      </button>
                      <button onClick={() => { setEditingId(item.id); setEditTitle(item.title); setEditContent(item.content || '') }} className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all" title="수정"><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => { if (confirm('이 자료를 삭제하시겠습니까?')) deleteMutation.mutate(item.id) }} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all" title="삭제"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {item.content && <p className="mt-3 text-gray-600 text-sm whitespace-pre-line line-clamp-3">{item.content}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && <div className="text-center text-sm text-gray-500">총 {items.length}개의 자료</div>}
      {showCrawl && <CrawlModal title={crawlTitle} content={crawlContent} onClose={() => setShowCrawl(false)} />}
      {accessItem && <AccessControlModal item={accessItem} onClose={() => setAccessItem(null)} onUpdate={onInvalidate} />}
    </>
  )
}

// ─── Image Tab ───

function ImageTab({ items, onInvalidate }: { items: SharedMediaResponse[]; onInvalidate: () => void }) {
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploadDescription, setUploadDescription] = useState('')
  const [uploadVisibility, setUploadVisibility] = useState<SharedMediaVisibility>('global')
  const [uploadProgress, setUploadProgress] = useState(false)
  const [previewItem, setPreviewItem] = useState<SharedMediaResponse | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [accessItem, setAccessItem] = useState<SharedMediaResponse | null>(null)

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!uploadFile) throw new Error('파일을 선택하세요')
      if (!uploadTitle.trim()) throw new Error('제목을 입력하세요')
      setUploadProgress(true)
      const response = await sharedMediaApi.uploadFile(uploadFile, uploadTitle.trim(), 'image', uploadVisibility, uploadDescription.trim() || undefined)
      return response.data.data
    },
    onSuccess: (data) => {
      onInvalidate()
      closeUploadModal()
      if (data?.visibility === 'specific') {
        setAccessItem(data)
        toast.success('이미지가 업로드되었습니다. 공개할 조직을 선택하세요.')
      } else {
        toast.success('이미지가 업로드되었습니다.')
      }
    },
    onError: (error: any) => toast.error(error?.response?.data?.error?.message || error.message || '업로드에 실패했습니다.'),
    onSettled: () => setUploadProgress(false),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, title, description }: { id: number; title: string; description: string }) => {
      const response = await sharedMediaApi.update(id, { title, description })
      return response.data.data
    },
    onSuccess: () => { onInvalidate(); toast.success('수정되었습니다.'); setEditingId(null) },
    onError: () => toast.error('수정에 실패했습니다.'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => { await sharedMediaApi.delete(id) },
    onSuccess: () => { onInvalidate(); toast.success('삭제되었습니다.') },
    onError: () => toast.error('삭제에 실패했습니다.'),
  })

  const closeUploadModal = () => {
    setShowUploadModal(false)
    setUploadFile(null)
    setUploadTitle('')
    setUploadDescription('')
    setUploadVisibility('global')
    setUploadProgress(false)
  }

  return (
    <>
      <div className="flex justify-end">
        <button onClick={() => { setUploadFile(null); setUploadTitle(''); setUploadDescription(''); setShowUploadModal(true) }} className="flex items-center px-4 py-2 bg-amber-500 text-slate-900 font-medium rounded-lg hover:bg-amber-600 shadow-sm">
          <Upload className="w-5 h-5 mr-2" />
          이미지 업로드
        </button>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={ImageIcon} label="이미지가 없습니다" actionLabel="첫 이미지 업로드" onAction={() => setShowUploadModal(true)} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map(item => (
            <div key={item.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group hover:shadow-md transition-shadow">
              {/* Image Preview */}
              <div className="relative bg-gray-100 aspect-video flex items-center justify-center cursor-pointer overflow-hidden" onClick={() => setPreviewItem(item)}>
                {item.streamUrl ? (
                  <img src={item.streamUrl} alt={item.title} className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-12 h-12 text-gray-300" />
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Eye className="w-6 h-6 text-gray-700" />
                  </div>
                </div>
                <div className="absolute bottom-2 right-2 text-xs text-white/70">
                  <span className="bg-black/50 px-2 py-0.5 rounded">{formatFileSize(item.fileSize)}</span>
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                {editingId === item.id ? (
                  <div className="space-y-3">
                    <input type="text" value={editTitle} onChange={e => setEditTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500" placeholder="제목" autoFocus />
                    <textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none h-16 focus:ring-2 focus:ring-amber-500 focus:border-amber-500" placeholder="설명 (선택)" />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100">취소</button>
                      <button onClick={() => updateMutation.mutate({ id: item.id, title: editTitle, description: editDescription })} disabled={updateMutation.isPending} className="flex items-center px-3 py-1.5 text-sm bg-amber-500 text-slate-900 font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50">
                        <Save className="w-3.5 h-3.5 mr-1" />저장
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="font-semibold text-gray-900 mb-1 truncate" title={item.title}>{item.title}</h3>
                    {item.description && <p className="text-sm text-gray-500 mb-2 line-clamp-2">{item.description}</p>}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-400">
                          {new Date(item.createdAt).toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </p>
                        <VisibilityBadge visibility={item.visibility} onClick={() => setAccessItem(item)} />
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => { setEditingId(item.id); setEditTitle(item.title); setEditDescription(item.description || '') }} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg" title="수정"><Edit3 className="w-4 h-4" /></button>
                        <button onClick={() => { if (confirm('이 이미지를 삭제하시겠습니까?')) deleteMutation.mutate(item.id) }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="삭제"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && <div className="text-center text-sm text-gray-500">총 {items.length}개의 이미지</div>}

      {showUploadModal && (
        <FileUploadModal
          mediaType="image"
          file={uploadFile}
          title={uploadTitle}
          description={uploadDescription}
          visibility={uploadVisibility}
          uploading={uploadProgress}
          onFileSelect={setUploadFile}
          onTitleChange={setUploadTitle}
          onDescriptionChange={setUploadDescription}
          onVisibilityChange={setUploadVisibility}
          onUpload={() => uploadMutation.mutate()}
          onClose={closeUploadModal}
        />
      )}

      {previewItem && <ImagePreviewModal item={previewItem} onClose={() => setPreviewItem(null)} />}
      {accessItem && <AccessControlModal item={accessItem} onClose={() => setAccessItem(null)} onUpdate={onInvalidate} />}
    </>
  )
}

// ─── File Tab ───

function FileTab({ items, onInvalidate }: { items: SharedMediaResponse[]; onInvalidate: () => void }) {
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploadDescription, setUploadDescription] = useState('')
  const [uploadVisibility, setUploadVisibility] = useState<SharedMediaVisibility>('global')
  const [uploading, setUploading] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [accessItem, setAccessItem] = useState<SharedMediaResponse | null>(null)

  const closeUploadModal = () => {
    setShowUploadModal(false)
    setUploadFile(null)
    setUploadTitle('')
    setUploadDescription('')
    setUploadVisibility('global')
    setUploading(false)
  }

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!uploadFile) throw new Error('파일을 선택하세요')
      if (!uploadTitle.trim()) throw new Error('제목을 입력하세요')
      setUploading(true)
      const response = await sharedMediaApi.uploadFile(
        uploadFile,
        uploadTitle.trim(),
        'file',
        uploadVisibility,
        uploadDescription.trim() || undefined,
      )
      return response.data.data
    },
    onSuccess: (data) => {
      onInvalidate()
      closeUploadModal()
      if (data?.visibility === 'specific') {
        setAccessItem(data)
        toast.success('파일이 업로드되었습니다. 공개할 조직을 선택하세요.')
      } else {
        toast.success('파일이 업로드되었습니다.')
      }
    },
    onError: (error: unknown) => {
      const message = (error as { response?: { data?: { error?: { message?: string } } }; message?: string })
      toast.error(message.response?.data?.error?.message || message.message || '업로드에 실패했습니다.')
    },
    onSettled: () => setUploading(false),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => sharedMediaApi.delete(id),
    onSuccess: () => { onInvalidate(); toast.success('파일이 삭제되었습니다.') },
    onError: () => toast.error('파일 삭제에 실패했습니다.'),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, title, description }: { id: number; title: string; description: string }) => {
      const response = await sharedMediaApi.update(id, { title, description })
      return response.data.data
    },
    onSuccess: () => {
      onInvalidate()
      setEditingId(null)
      toast.success('파일 정보가 수정되었습니다.')
    },
    onError: () => toast.error('파일 정보 수정에 실패했습니다.'),
  })

  const startEditing = (item: SharedMediaResponse) => {
    setEditingId(item.id)
    setEditTitle(item.title)
    setEditDescription(item.description || '')
  }

  const saveEdit = (id: number) => {
    const title = normalizeSharedMediaTitle(editTitle)
    if (!title) {
      toast.error('제목을 입력하세요.')
      return
    }

    updateMutation.mutate({ id, title, description: editDescription.trim() })
  }

  return (
    <>
      <div className="flex justify-end">
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center px-4 py-2 bg-amber-500 text-slate-900 font-medium rounded-lg hover:bg-amber-600 shadow-sm"
        >
          <Upload className="w-5 h-5 mr-2" />
          파일 업로드
        </button>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={FileIcon} label="파일이 없습니다" actionLabel="첫 파일 업로드" onAction={() => setShowUploadModal(true)} />
      ) : (
        <div className="space-y-3">
          {items.map(item => (
            <div key={item.id} className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-100">
                <FileIcon className="h-6 w-6 text-slate-500" />
              </div>
              {editingId === item.id ? (
                <div className="min-w-0 flex-1 space-y-3">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <label htmlFor={`file-title-${item.id}`} className="mb-1 block text-sm font-medium text-gray-700">제목 *</label>
                      <input
                        id={`file-title-${item.id}`}
                        type="text"
                        value={editTitle}
                        onChange={event => setEditTitle(event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label htmlFor={`file-description-${item.id}`} className="mb-1 block text-sm font-medium text-gray-700">설명</label>
                      <input
                        id={`file-description-${item.id}`}
                        type="text"
                        value={editDescription}
                        onChange={event => setEditDescription(event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500"
                        placeholder="설명을 입력하세요 (선택)"
                      />
                    </div>
                  </div>
                  <p className="truncate text-sm text-gray-500">{item.originalFilename || '파일'} · {formatFileSize(item.fileSize)}</p>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      disabled={updateMutation.isPending}
                      className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-50"
                    >
                      취소
                    </button>
                    <button
                      type="button"
                      onClick={() => saveEdit(item.id)}
                      disabled={updateMutation.isPending || !normalizeSharedMediaTitle(editTitle)}
                      className="flex items-center rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {updateMutation.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
                      저장
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-semibold text-gray-900">{item.title}</h3>
                    <p className="truncate text-sm text-gray-500">{item.originalFilename || '파일'} · {formatFileSize(item.fileSize)}</p>
                    {item.description && <p className="mt-1 line-clamp-1 text-sm text-gray-500">{item.description}</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-1 sm:flex-nowrap">
                    <VisibilityBadge visibility={item.visibility} onClick={() => setAccessItem(item)} />
                    {item.streamUrl && (
                      <a
                        href={item.streamUrl}
                        download={item.originalFilename || item.title}
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-50"
                      >
                        <Download className="h-4 w-4" />
                        다운로드
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => startEditing(item)}
                      className="rounded-lg p-2 text-gray-400 hover:bg-amber-50 hover:text-amber-600"
                      title="수정"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => { if (confirm('이 파일을 삭제하시겠습니까?')) deleteMutation.mutate(item.id) }}
                      className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                      title="삭제"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && <div className="text-center text-sm text-gray-500">총 {items.length}개의 파일</div>}
      {showUploadModal && (
        <FileUploadModal
          mediaType="file"
          file={uploadFile}
          title={uploadTitle}
          description={uploadDescription}
          visibility={uploadVisibility}
          uploading={uploading}
          onFileSelect={setUploadFile}
          onTitleChange={setUploadTitle}
          onDescriptionChange={setUploadDescription}
          onVisibilityChange={setUploadVisibility}
          onUpload={() => uploadMutation.mutate()}
          onClose={closeUploadModal}
        />
      )}
      {accessItem && <AccessControlModal item={accessItem} onClose={() => setAccessItem(null)} onUpdate={onInvalidate} />}
    </>
  )
}

// ─── Shared Components ───

function EmptyState({ icon: Icon, label, actionLabel, onAction }: {
  icon: typeof Video; label: string; actionLabel: string; onAction: () => void
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
      <Icon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">{label}</h3>
      <button onClick={onAction} className="inline-flex items-center px-4 py-2 bg-amber-500 text-slate-900 font-medium rounded-lg hover:bg-amber-600">
        <Plus className="w-4 h-4 mr-2" />
        {actionLabel}
      </button>
    </div>
  )
}

function VisibilityBadge({ visibility, onClick }: { visibility: SharedMediaVisibility; onClick: () => void }) {
  const config: Record<SharedMediaVisibility, { label: string; title: string; className: string }> = {
    global: { label: '전체', title: '전체 공개', className: 'bg-green-100 text-green-700 hover:bg-green-200' },
    company_all: { label: 'Company', title: 'Company 전체', className: 'bg-blue-100 text-blue-700 hover:bg-blue-200' },
    learning_center_all: { label: '러닝센터', title: '러닝센터 전체', className: 'bg-violet-100 text-violet-700 hover:bg-violet-200' },
    specific: { label: '지정', title: '지정 조직', className: 'bg-orange-100 text-orange-700 hover:bg-orange-200' },
  }
  const current = config[visibility]
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick() }}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${current.className}`}
      title={current.title}
    >
      {visibility === 'global' ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
      {current.label}
    </button>
  )
}

// ─── File Upload Modal ───

function FileUploadModal({ mediaType, file, title, description, visibility, uploading, onFileSelect, onTitleChange, onDescriptionChange, onVisibilityChange, onUpload, onClose }: {
  mediaType: 'video' | 'image' | 'file'
  file: File | null; title: string; description: string; visibility: SharedMediaVisibility; uploading: boolean
  onFileSelect: (f: File | null) => void; onTitleChange: (v: string) => void; onDescriptionChange: (v: string) => void
  onVisibilityChange: (v: SharedMediaVisibility) => void
  onUpload: () => void; onClose: () => void
}) {
  const [dragOver, setDragOver] = useState(false)

  const isVideo = mediaType === 'video'
  const isImage = mediaType === 'image'
  const acceptTypes = isVideo
    ? 'video/mp4,video/webm,video/quicktime,video/x-msvideo,video/mpeg'
    : isImage ? 'image/png,image/jpeg,image/gif,image/svg+xml,image/webp' : '*/*'
  const typeLabel = isVideo ? '동영상' : isImage ? '이미지' : '파일'
  const formatLabel = isVideo
    ? 'MP4, WebM, MOV, AVI, MPEG (최대 500MB)'
    : isImage ? 'PNG, JPG, GIF, SVG, WebP (최대 10MB)' : `모든 파일 형식 (파일당 최대 ${MAX_GENERAL_FILE_SIZE_MB}MB)`
  const maxSize = isVideo ? 500 * 1024 * 1024 : isImage ? 10 * 1024 * 1024 : MAX_GENERAL_FILE_SIZE_MB * 1024 * 1024
  const maxSizeLabel = isVideo ? '500MB' : isImage ? '10MB' : `${MAX_GENERAL_FILE_SIZE_MB}MB`

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const droppedFile = e.dataTransfer.files[0]
    if (!droppedFile) return
    const prefix = isVideo ? 'video/' : 'image/'
    if (mediaType !== 'file' && !droppedFile.type.startsWith(prefix)) {
      toast.error(`${typeLabel} 파일만 업로드할 수 있습니다.`)
      return
    }
    if (droppedFile.size > maxSize) {
      toast.error(`파일 크기가 ${maxSizeLabel}를 초과합니다.`)
      return
    }
    onFileSelect(droppedFile)
    if (!title) onTitleChange(droppedFile.name.replace(/\.[^.]+$/, ''))
  }, [isVideo, maxSize, maxSizeLabel, mediaType, onFileSelect, onTitleChange, title, typeLabel])

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="max-h-[calc(100vh-2rem)] w-full max-w-lg mx-4 overflow-y-auto rounded-2xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Upload className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{typeLabel} 업로드</h3>
              <p className="text-xs text-gray-500">{formatLabel}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" disabled={uploading}><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        <div className="p-6 space-y-4">
          {!file ? (
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${dragOver ? 'border-amber-500 bg-amber-50' : 'border-gray-300 hover:border-gray-400'}`}
              onClick={() => {
                const input = document.createElement('input')
                input.type = 'file'
                input.accept = acceptTypes
                input.onchange = (e) => {
                  const f = (e.target as HTMLInputElement).files?.[0]
                  if (f) {
                    if (f.size > maxSize) { toast.error(`파일 크기가 ${maxSizeLabel}를 초과합니다.`); return }
                    onFileSelect(f)
                    if (!title) onTitleChange(f.name.replace(/\.[^.]+$/, ''))
                  }
                }
                input.click()
              }}
            >
              {isVideo ? <Video className="w-12 h-12 text-gray-300 mx-auto mb-3" /> : isImage ? <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" /> : <FileIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />}
              <p className="text-gray-600 font-medium">파일을 드래그하거나 클릭하여 선택</p>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-slate-900 flex items-center justify-center flex-shrink-0">
                {isVideo ? <FileVideo className="w-6 h-6 text-white" /> : isImage ? <ImageIcon className="w-6 h-6 text-white" /> : <FileIcon className="w-6 h-6 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{file.name}</p>
                <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                  <span className="flex items-center gap-1"><HardDrive className="w-3 h-3" />{formatFileSize(file.size)}</span>
                  <span>{file.type.split('/')[1]?.toUpperCase()}</span>
                </div>
              </div>
              {!uploading && <button onClick={() => onFileSelect(null)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded"><X className="w-4 h-4" /></button>}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">제목 *</label>
            <input type="text" value={title} onChange={e => onTitleChange(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" placeholder={`${typeLabel} 제목을 입력하세요`} disabled={uploading} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
            <textarea value={description} onChange={e => onDescriptionChange(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none h-20 focus:ring-2 focus:ring-amber-500 focus:border-amber-500" placeholder="설명을 입력하세요 (선택)" disabled={uploading} />
          </div>
          <fieldset disabled={uploading}>
            <legend className="mb-2 text-sm font-medium text-gray-700">공개 범위</legend>
            <div className="grid grid-cols-2 gap-2">
              {VISIBILITY_OPTIONS.map(option => (
                <label
                  key={option.value}
                  className={`cursor-pointer rounded-lg border-2 px-3 py-2.5 transition-colors ${
                    visibility === option.value
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                    <input
                      type="radio"
                      name="upload-visibility"
                      value={option.value}
                      checked={visibility === option.value}
                      onChange={() => onVisibilityChange(option.value)}
                      className="text-amber-500 focus:ring-amber-500"
                    />
                    {option.label}
                  </span>
                  <span className="mt-1 block pl-6 text-xs leading-4 text-gray-500">{option.description}</span>
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100" disabled={uploading}>취소</button>
          <button onClick={onUpload} disabled={!file || !title.trim() || uploading} className="flex items-center px-5 py-2 bg-amber-500 text-slate-900 font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm">
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />업로드 중...</> : <><Upload className="w-4 h-4 mr-2" />업로드</>}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Video Player Modal ───

function VideoPlayerModal({ item, onClose }: { item: SharedMediaResponse; onClose: () => void }) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90" onClick={onClose}>
      <div className="relative w-full max-w-5xl mx-4" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors" title="닫기 (ESC)"><X className="w-6 h-6" /></button>
        <div className="absolute -top-12 left-0 text-white/90 text-lg font-medium truncate max-w-md">{item.title}</div>
        <div className="bg-black rounded-xl overflow-hidden shadow-2xl">
          {item.streamUrl ? (
            <video src={item.streamUrl} controls autoPlay className="w-full aspect-video" controlsList="nodownload">브라우저가 비디오 재생을 지원하지 않습니다.</video>
          ) : (
            <div className="w-full aspect-video flex items-center justify-center text-white/50"><Video className="w-16 h-16 opacity-50" /></div>
          )}
        </div>
        <div className="mt-3 flex items-center gap-4 text-sm text-white/60">
          <span>{item.originalFilename}</span>
          <span>{formatFileSize(item.fileSize)}</span>
          {item.durationSeconds && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{formatDuration(item.durationSeconds)}</span>}
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Image Preview Modal ───

function ImagePreviewModal({ item, onClose }: { item: SharedMediaResponse; onClose: () => void }) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90" onClick={onClose}>
      <div className="relative max-w-5xl max-h-[90vh] mx-4" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors" title="닫기 (ESC)"><X className="w-6 h-6" /></button>
        <div className="absolute -top-12 left-0 text-white/90 text-lg font-medium truncate max-w-md">{item.title}</div>
        {item.streamUrl ? (
          <img src={item.streamUrl} alt={item.title} className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl" />
        ) : (
          <div className="w-96 h-64 flex items-center justify-center text-white/50"><ImageIcon className="w-16 h-16 opacity-50" /></div>
        )}
        <div className="mt-3 flex items-center gap-4 text-sm text-white/60">
          <span>{item.originalFilename}</span>
          <span>{formatFileSize(item.fileSize)}</span>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Access Control Modal ───

function AccessControlModal({ item, onClose, onUpdate }: {
  item: SharedMediaResponse; onClose: () => void; onUpdate: () => void
}) {
  const [visibility, setVisibility] = useState<SharedMediaVisibility>(item.visibility)
  const [orgs, setOrgs] = useState<OrganizationSimple[]>([])
  const [accessOrgs, setAccessOrgs] = useState<SharedMediaAccessOrg[]>([])
  const [selectedOrgIds, setSelectedOrgIds] = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState(true)
  const visibilityOptions: { value: SharedMediaVisibility; label: string; activeClassName: string }[] = [
    { value: 'global', label: '전체 공개', activeClassName: 'border-green-500 bg-green-50 text-green-700' },
    { value: 'company_all', label: 'Company 전체', activeClassName: 'border-blue-500 bg-blue-50 text-blue-700' },
    { value: 'learning_center_all', label: '러닝센터 전체', activeClassName: 'border-violet-500 bg-violet-50 text-violet-700' },
    { value: 'specific', label: '지정 조직', activeClassName: 'border-orange-500 bg-orange-50 text-orange-700' },
  ]

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [orgList, accessList] = await Promise.all([
        adminApi.getAllOrganizations(),
        sharedMediaApi.getAccess(item.id).then((r): SharedMediaAccessOrg[] => r.data.data || []),
      ])
      setOrgs(orgList)
      setAccessOrgs(accessList)
      setSelectedOrgIds(new Set(accessList.map(a => a.organizationId)))
    } catch {
      toast.error('데이터 로딩에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      // 1) visibility 변경
      if (visibility !== item.visibility) {
        await sharedMediaApi.updateVisibility(item.id, visibility)
      }

      // 2) specific인 경우 접근 권한 동기화
      if (visibility === 'specific') {
        const currentIds = new Set(accessOrgs.map(a => a.organizationId))
        const toAdd = [...selectedOrgIds].filter(id => !currentIds.has(id))
        const toRemove = [...currentIds].filter(id => !selectedOrgIds.has(id))

        if (toAdd.length > 0) {
          await sharedMediaApi.grantAccess(item.id, toAdd)
        }
        for (const orgId of toRemove) {
          await sharedMediaApi.revokeAccess(item.id, orgId)
        }
      }

      toast.success('접근 권한이 저장되었습니다.')
      onUpdate()
      onClose()
    } catch {
      toast.error('접근 권한 저장에 실패했습니다.')
    }
  }

  const toggleOrg = (orgId: number) => {
    setSelectedOrgIds(prev => {
      const next = new Set(prev)
      if (next.has(orgId)) {
        next.delete(orgId)
      } else {
        next.add(orgId)
      }
      return next
    })
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">접근 권한 설정</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">자료: <span className="font-medium text-gray-900">{item.title}</span></p>
          </div>

          {/* Visibility Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">공개 범위</label>
            <div className="grid grid-cols-2 gap-2">
              {visibilityOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => setVisibility(option.value)}
                  className={`flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                    visibility === option.value
                      ? option.activeClassName
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {option.value === 'global' ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Org list (only when specific) */}
          {visibility === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">접근 가능 조직</label>
              {loading ? (
                <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
              ) : orgs.length === 0 ? (
                <p className="text-sm text-gray-500">등록된 조직이 없습니다.</p>
              ) : (
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-100">
                  {orgs.map(org => (
                    <label key={org.id} className="flex items-center px-4 py-2.5 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedOrgIds.has(org.id)}
                        onChange={() => toggleOrg(org.id)}
                        className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                      />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{org.displayName || org.name}</p>
                        <p className="text-xs text-gray-500">
                          {org.organizationType === 'learning_center' ? '러닝센터' : org.organizationType === 'company' ? 'Company' : org.organizationType}
                          {org.subdomain ? ` · ${org.subdomain}` : ''}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100">취소</button>
          <button onClick={handleSave} className="px-5 py-2 bg-amber-500 text-slate-900 font-medium rounded-lg hover:bg-amber-600 shadow-sm">저장</button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Star Wars Crawl Modal ───

function CrawlModal({ title, content, onClose }: { title: string; content: string; onClose: () => void }) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [audioLoaded, setAudioLoaded] = useState(false)

  const BGM_URL = 'https://cdn.pixabay.com/audio/2022/01/18/audio_d0a13f69d2.mp3'

  useEffect(() => {
    const audio = new Audio(BGM_URL)
    audio.loop = true
    audio.volume = 0.3
    audioRef.current = audio
    audio.addEventListener('canplaythrough', () => setAudioLoaded(true))
    audio.addEventListener('error', () => setAudioLoaded(false))
    return () => { audio.pause(); audio.src = ''; audioRef.current = null }
  }, [])

  const toggleMusic = () => {
    if (!audioRef.current) return
    if (isPlaying) { audioRef.current.pause(); setIsPlaying(false) }
    else { audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {}) }
  }

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  const lines = content.split('\n')

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center">
      <div className="absolute top-6 right-6 z-10 flex items-center gap-3">
        {audioLoaded && (
          <button onClick={toggleMusic} className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors" title={isPlaying ? '음악 끄기' : '음악 켜기'}>
            {isPlaying ? <Music className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
          </button>
        )}
        <button onClick={onClose} className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors" title="닫기 (ESC)"><X className="w-6 h-6" /></button>
      </div>

      <div className="w-full h-full overflow-hidden relative" style={{ perspective: '600px' }}>
        <div className="absolute top-0 left-0 right-0 z-10 pointer-events-none" style={{ height: '30%', background: 'linear-gradient(to bottom, black 0%, transparent 100%)' }} />
        <div className="absolute bottom-0 left-0 right-0 z-10 pointer-events-none" style={{ height: '20%', background: 'linear-gradient(to top, black 0%, transparent 100%)' }} />
        <div className="absolute left-1/2" style={{ width: '90vw', maxWidth: '1800px', transform: 'translateX(-50%) rotateX(20deg)', transformOrigin: 'center bottom', top: '85%', animation: `crawl ${Math.max(lines.length * 4, 40)}s linear forwards` }}>
          <h2 className="text-center mb-20 font-bold" style={{ fontSize: 'clamp(8rem, 18vw, 14rem)', color: '#fbbf24', textShadow: '0 0 40px rgba(251, 191, 36, 0.5)', letterSpacing: '0.12em' }}>{title}</h2>
          <div className="px-16">
            {lines.map((line, i) => (
              <p key={i} className="text-center leading-relaxed" style={{ fontSize: 'clamp(5rem, 10vw, 8rem)', color: '#fbbf24', textShadow: '0 0 20px rgba(251, 191, 36, 0.3)', marginBottom: line.trim() === '' ? '2.5em' : '0.8em', letterSpacing: '0.06em' }}>
                {line || '\u00A0'}
              </p>
            ))}
          </div>
          <div style={{ height: '60vh' }} />
        </div>
      </div>

      <style>{`
        @keyframes crawl {
          0% { top: 85%; }
          100% { top: -300%; }
        }
      `}</style>
    </div>,
    document.body
  )
}
